import { Box, Grid, GridProps, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import useBreakpoints from '../../hooks/useBreakpoints';
import { selectDisplayedOutcomes, type FeaturedEventCardModel } from '../../hooks/useFeaturedEventCardModel';
import { mergeSx } from '../../utils/styles';
import EventCardOutcomeCell from './EventCardOutcomeCell';
import EventCardParticipant from './EventCardParticipant';
import { useSkinStyles } from '../../hooks/useSkinStyles';
import { EVENT_SKIN_STYLES } from '../../styles/components/eventCard';
import UpcomingStatsButton from '../atoms/UpcomingStatsButton';

type EventCardBaseProps = {
  model: FeaturedEventCardModel;
} & GridProps;

const EventCardBase = ({ model, ...gridProps }: EventCardBaseProps) => {
  const { isMobile } = useBreakpoints();
  const styles = useSkinStyles(EVENT_SKIN_STYLES);
  const displayedOutcomes = selectDisplayedOutcomes(model);
  const hasMobileStatistics = isMobile && model.hasStatistics;

  return (
    <Grid
      container
      {...gridProps}
      sx={mergeSx(
        {
          alignItems: 'center',
        },
        mergeSx(styles.container, gridProps.sx),
        gridProps.sx
      )}
    >
      <Grid
        data-event-card-section='content'
        sx={mergeSx(styles.content, hasMobileStatistics ? styles.contentWithMobileStatistics : undefined)}
        onClick={model.handleContentClick}
        size={12}
      >
        <Stack sx={styles.eventParticipants}>
          <Box sx={styles.participantContainer}>
            <EventCardParticipant
              competitionName={model.competitionName}
              logoPlacement='top'
              participant={model.participants.home}
              sportId={model.sportId}
              sx={mergeSx(styles.participant, { justifyContent: { xs: 'start', md: 'end' } })}
            />

            {!isMobile && (
              <Box
                sx={styles.desktopFeaturedStatsButton}
                data-testid='event-card-desktop-stats-slot'
                onClick={(event) => event.stopPropagation()}
              >
                {model.hasStatistics ? (
                  <UpcomingStatsButton
                    statsId={model.statsId}
                    sportId={model.sportId}
                    event={model.statsEvent}
                    iconSx={styles.featuredStatsButtonIcon}
                  />
                ) : (
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'neutral.300',
                    }}
                  >
                    v
                  </Typography>
                )}
              </Box>
            )}

            <EventCardParticipant
              competitionName={model.competitionName}
              logoPlacement='top'
              participant={model.participants.away}
              sportId={model.sportId}
              sx={mergeSx(styles.participant, {
                justifyContent: 'start',
              })}
            />
          </Box>
        </Stack>
        <Typography variant='h6' sx={styles.startDate}>
          {dayjs(model.startDate).format('dddd HH:mm')}
        </Typography>
        {hasMobileStatistics && (
          <Box
            sx={mergeSx(styles.featuredStatsButton, styles.mobileFeaturedStatsButton)}
            data-testid='event-card-mobile-stats-slot'
            onClick={(event) => event.stopPropagation()}
          >
            <UpcomingStatsButton
              statsId={model.statsId}
              sportId={model.sportId}
              event={model.statsEvent}
              iconSx={styles.featuredStatsButtonIcon}
            />
          </Box>
        )}
      </Grid>
      <Grid container>
        {displayedOutcomes.map(({ id, outcome }) => {
          if (!outcome) {
            return (
              <Grid
                key={id}
                data-event-card-section='outcome'
                sx={mergeSx(
                  {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  },
                  styles.emptyOutcomeGrid
                )}
                size={12 / displayedOutcomes.length}
              >
                <Box sx={styles.emptyOutcomeGridWrapper}>
                  <Box sx={styles.emptyOutcome}>
                    <Typography
                      sx={{
                        textAlign: 'center',
                      }}
                    >
                      -
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            );
          }
          return (
            <EventCardOutcomeCell
              key={outcome.id}
              outcomeCount={displayedOutcomes.length}
              outcome={outcome}
              onClick={() => model.addToBetslip(outcome)}
            />
          );
        })}
      </Grid>
    </Grid>
  );
};

export default EventCardBase;
